import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded'
import type { HelperReadinessModel } from './helperReadinessModel'
import { HELPER_BLOCKER_REASON_LABELS } from '../../types/helperBlocker'
import { useNls } from '../../nls/nlsProvider'
import { Button, StatusChip, type StatusPillTone } from '../Common'

const toneClass = (severity: HelperReadinessModel['status']) => {
  if (severity === 'blocked') return 'border-[var(--danger)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]'
  if (severity === 'warning') return 'border-[var(--warning)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]'
  return 'border-[var(--border-subtle)] bg-[var(--surface-card)] text-[var(--text-strong)]'
}

const chipTone = (severity: 'clear' | 'warning' | 'blocked'): StatusPillTone => {
  if (severity === 'blocked') return 'danger'
  if (severity === 'warning') return 'warning'
  return 'neutral'
}

export const HelperReadinessStrip = ({
  readiness,
  compact = false,
  onReportBlocker,
  onRequestReplenishment,
}: {
  readiness: HelperReadinessModel
  compact?: boolean
  onReportBlocker?: () => void
  onRequestReplenishment?: () => void
}) => {
  const { t } = useNls()
  if (!readiness.hasAnySignal) return null
  const visibleSignals = readiness.signals.slice(0, compact ? 2 : 4)
  return (
    <div
      className={`rounded-2xl border px-3 py-2 ${toneClass(readiness.status)}`}
      data-testid="helper-readiness-strip"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">
            {t('NLSHelperReadinessTitle')}
          </p>
          <p className="mt-0.5 truncate text-xs font-medium">
            {readiness.openBlockers.length > 0
              ? t('NLSHelperReadinessBlockedCount', { count: readiness.openBlockers.length })
              : readiness.needsStock
                ? t('NLSHelperReadinessStockShort')
                : readiness.status === 'warning'
                  ? t('NLSHelperReadinessNeedsAttention')
                  : t('NLSHelperReadinessReady')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {readiness.needsStock && onRequestReplenishment ? (
            <Button
              variant="secondary"
              size="md"
              onClick={(event) => {
                event.stopPropagation()
                onRequestReplenishment()
              }}
              data-testid="helper-need-more"
              icon={<Inventory2RoundedIcon style={{ fontSize: 14 }} />}
            >
              {t('NLSHelperReadinessNeedMore')}
            </Button>
          ) : null}
          {onReportBlocker ? (
            <Button
              variant="tertiary"
              size="md"
              onClick={(event) => {
                event.stopPropagation()
                onReportBlocker()
              }}
              data-testid="helper-report-blocker"
              icon={<ReportGmailerrorredRoundedIcon style={{ fontSize: 14 }} />}
            >
              {t('NLSHelperReadinessReport')}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {readiness.dueLabel ? (
          <span className="rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-2 py-1 text-[11px] text-[var(--muted)]">
            {readiness.dueLabel}
          </span>
        ) : null}
        {readiness.requirementLabels.map((label) => (
          <span key={label} className="rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-2 py-1 text-[11px] text-[var(--muted)]">
            {label}
          </span>
        ))}
        {visibleSignals.map((signal) => (
          <StatusChip key={`${signal.kind}-${signal.summary}`} tone={chipTone(signal.severity)} size="sm">
            {signal.summary}
          </StatusChip>
        ))}
        {readiness.openBlockers.map((blocker) => (
          <span key={blocker.id} className="rounded-full border border-[var(--danger)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] px-2 py-1 text-[11px] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">
            {HELPER_BLOCKER_REASON_LABELS[blocker.reason]}
          </span>
        ))}
        {!compact && readiness.dependencyReasons.map((reason) => (
          <span key={reason} className="rounded-full border border-[var(--danger)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] px-2 py-1 text-[11px] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">
            {reason}
          </span>
        ))}
      </div>
    </div>
  )
}
